import { Key, ReactNode, useEffect, useState } from 'react';
import type {
  GetStaticPropsContext,
  InferGetStaticPropsType,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { translateWithSelfFallback } from '@i18n/translateWithSelfFallback';
import cn from 'classnames';
import {
  getContentPageByUid,
  getContentPageNavigation,
} from '@adapters/prismic/fetching/content';
import {
  ContentPageNavigationItem,
  ContentPageNavigationHeading,
} from '@adapters/prismic/types/contentPage';
import { getRecentBlogPosts } from '@adapters/prismic/fetching/blog';
import { BlogPost } from '@adapters/prismic/types/blogPost';
import prismicLinkResolver from '@adapters/prismic/utils/linkResolver';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import Layout from '@components/global/Layout';
import Container from '@components/global/Container';
import Sidebar from '@components/global/Sidebar';
import BannerImage from '@components/ui/BannerImage';
import IconWrapper from '@components/ui/Icons';
import SliceHandler from '@components/cms/SliceHandler';
import CardLink from '@components/cms/CardLink';
import BlogArticlesGrid from '@components/cms/BlogArticlesGrid';
import ContentPageSidebar from '@components/content/ContentPageSidebar';
import ContentPageHeading from '@components/content/ContentPageHeading';
import ContentPageContent from '@components/content/ContentPageContent';
import ContentPageNavigation from '@components/content/ContentPageNavigation';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MEC_TITLE } from '@utils/constants';
import { handlePromoDetailsTracking } from '@utils/gtm';
import { StructuredArticleData } from '@components/StructuredData/StructuredArticleData';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './ContentPage.module.scss';
import {
  ActionsSlice,
  MarkdownCopySlice,
  ImageWithBlockSlice,
  ImageWithCaptionSlice,
  GenericSlice,
} from '@adapters/prismic/types/slices';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

interface ContentPageProps {
  preview: boolean;
  alternateLocaleUrl?: string;
  contentPage: any;
  canonicalUrl: string;
  navigationStructure: ContentPageNavigationItem[] | null;
  navigationHeading: ContentPageNavigationHeading | null;
  recentBlogPosts: BlogPost[] | null;
  promoMessages?: PromoTicker[];
  stores: any;
}

export const getStaticProps = async ({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<ContentPageProps>
> => {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Content page has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const [
    contentPage,
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    recentBlogPosts,
    promoBarData,
  ] = await Promise.allSettled([
    getContentPageByUid(params.slug, pageLocale, previewRef),
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'promotions']),
    getRecentBlogPosts(pageLocale, previewRef),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  if (!contentPage) {
    console.warn(`Content page with slug '${params?.slug}' not found`);
    return {
      notFound: true,
    };
  }

  const contentPageNavigation = await getContentPageNavigation(
    contentPage,
    pageLocale,
    previewRef
  );

  const navigationHeading = contentPageNavigation?.navigationHeading ?? null;
  const navigationStructure =
    contentPageNavigation?.navigationStructure ?? null;

  const canonicalUrl = process.env.BASE_URL + prismicLinkResolver(contentPage);

  return {
    props: {
      contentPage,
      canonicalUrl,
      navigationStructure,
      navigationHeading,
      preview,
      recentBlogPosts,
      stores,
      ...(contentPage.alternate_languages.length && {
        alternateLocaleUrl: prismicLinkResolver(
          contentPage.alternate_languages[0]
        ),
      }),
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function ContentPageComponent({
  contentPage,
  canonicalUrl,
  navigationStructure,
  navigationHeading,
  recentBlogPosts,
}: InferGetStaticPropsType<typeof getStaticProps>): ReactNode {
  const { query, asPath } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { t } = useTranslation('common');
  const [showMobileNav, setShowMobileNav] = useState(false);
  // Close mobile nav when path changed
  useEffect(() => {
    setShowMobileNav(false);
  }, [asPath]);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  if (!contentPage) return null;
  const {
    data: { children },
  } = contentPage;
  const {
    data: {
      title,
      body,
      hero_image: heroImage,
      meta_title: metaTitle,
      meta_description: metaDescription,
    },
  } = contentPage;
  const hasContent = body?.length > 0;

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          locale,
          images: [
            {
              url: heroImage?.url,
              width: heroImage?.dimensions?.width,
              height: heroImage?.dimensions?.height,
              alt: heroImage?.alt,
            },
          ],
        }}
      />

      <StructuredArticleData data={contentPage?.data} />

      {heroImage?.url && <BannerImage image={heroImage} />}

      <Container
        className={cn(styles.contentContainer, {
          [styles.contentContainerNoContent]: !hasContent,
        })}
      >
        <ContentPageSidebar classNames={styles.contentPageSideBar}>
          {navigationHeading && navigationStructure && (
            <ContentPageNavigation
              heading={navigationHeading}
              navigationStructure={navigationStructure}
              classNames={styles.navigationDesktop}
            />
          )}

          <button
            type="button"
            className={styles.mobileNavigationButton}
            onClick={() => setShowMobileNav(true)}
          >
            <span>{title}</span>
            <IconWrapper icon="menu" width={24} height={24} />
          </button>

          <Sidebar
            open={showMobileNav}
            onClose={() => setShowMobileNav(false)}
            side="left"
            containerClassNames={styles.contentPageNavFlyout}
            label={translateWithSelfFallback(t, 'nav.utility.menu')}
          >
            <button
              className={styles.contentNavCloseButton}
              onClick={() => setShowMobileNav(false)}
              aria-label={t('nav.mobile.header.close.aria')}
              type="button"
            >
              <IconWrapper icon="close" width={24} height={24} />
            </button>
            {navigationHeading && navigationStructure && (
              <ContentPageNavigation
                heading={navigationHeading}
                navigationStructure={navigationStructure}
              />
            )}
          </Sidebar>
        </ContentPageSidebar>

        <ContentPageHeading
          classNames={cn({
            [styles.contentPageHeading]: heroImage?.url,
            [styles.contentPageHeadingNoHero]: !heroImage?.url,
          })}
        >
          {title}
        </ContentPageHeading>
        {hasContent && (
          <ContentPageContent classNames={styles.contentPageContent}>
            {body?.map(
              (
                slice:
                  | ActionsSlice
                  | MarkdownCopySlice
                  | ImageWithBlockSlice
                  | ImageWithCaptionSlice
                  | GenericSlice,
                i: number
              ) => (
                <SliceHandler
                  slice={slice}
                  pageType="content"
                  key={`slice-${i}`}
                />
              )
            )}
          </ContentPageContent>
        )}

        <ul className={styles.contentPageChildLinks}>
          {children.length > 0 &&
            children
              .filter(
                (childPage: { child: { uid: any } }) => !!childPage.child?.uid
              )
              .map(
                (childPage: {
                  child: {
                    uid: Key | null | undefined;
                    data: {
                      title: string;
                      teaser_copy: string;
                      hero_image: { url: string; alt: string };
                    };
                  };
                }) => (
                  <CardLink
                    key={childPage.child.uid}
                    title={childPage.child?.data?.title}
                    excerpt={childPage.child?.data?.teaser_copy}
                    imageUrl={childPage.child?.data?.hero_image?.url}
                    imageAlt={childPage.child?.data?.hero_image?.alt}
                    link={prismicLinkResolver(childPage?.child)}
                    hasBorder
                  />
                )
              )}
        </ul>
      </Container>

      <Container className={styles.recentBlogsContainer}>
        {recentBlogPosts && (
          <BlogArticlesGrid
            blogPosts={recentBlogPosts}
            title={t('recent.articles.heading')}
            linkTitle={t('recent.articles.button')}
          />
        )}
      </Container>
    </>
  );
}

ContentPageComponent.Layout = Layout;
